import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AmorgAdminToken, fieldValidation, getDefaultCompanyInfo, OrganizationListView, rng, SiteView } from "../../amorg-common";
import { createOrganization, createSite, getActiveUser, getAllSites, getOrganizations, updateOrganization } from '../../api/adminClientApi';
import { Button } from '../../designSystem/button/Button';
import { ButtonRow } from '../../designSystem/button/ButtonRow';
import { colors } from '../../designSystem/colors/color';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { TextField } from '../../designSystem/formField/text/TextField';
import { Icon } from '../../designSystem/icons/Icons';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { Modal } from '../../designSystem/Modal';
import { Table, TableRowWithOnClick } from '../../designSystem/Table';
import { AdminTab } from './_components/AdminTab';
import { ImpersonateModalButton } from './_components/ImpersonateModalButton';
import { AmorgAdminPageProvider } from './AmorgAdminPageContext';
import { InfoChip } from '../../designSystem/Chip';
import { SelectField } from '../../designSystem/formField/SelectField';
import { ErrorLabel } from '../../designSystem/Label';

export default function AmorgAdminSites (): JSX.Element 
{
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(true);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [sites, setSites] = React.useState<SiteView[]>([]);
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken | null>(null);

    useEffect(() =>
    {
        document.title = `Amorgy - Sites`;
        const init = async () =>
        {
            const token = await getActiveUser(() => navigate(`/amorgy/login`));
            setActiveUser(token);
            setSites(await getSites());
            setLoading(false);
        };
        init().catch(console.error);
    }, []);

    async function getSites ()
    {
        return await getAllSites(() => navigate(`/amorgy/login`));
    }

    const filteredSites = sites.filter(s => 
        s.loginName.toLowerCase().includes(searchTerm.toLowerCase()) || 
        s.organization?.companyInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        s.organization?.partner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.id.toLowerCase().includes(searchTerm.toLowerCase())
    );


    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminTab selectedTab='sites' />
                        <ButtonRow>
                            <CreateSiteModalButton 
                                activeUser={activeUser as AmorgAdminToken}
                                onCreated={async () => setSites(await getSites())} />
                        </ButtonRow>
                    </Flex>
                }

                contentContainer={

                    <Flex flexDirection='column' gap={16}>

                        <div style={{ maxWidth:'400px'}}>
                            <TextField
                                value={searchTerm}
                                onChange={(val) => setSearchTerm(val)}
                                hideLabel
                                leadingIcon="interface_actions_search"
                                placeholder="Søg" />
                        </div>

                        <Table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Site</th>
                                    <th>Organisation</th>
                                    <th>
                                        {activeUser && activeUser.partner===null &&
                                            <>Partner</>
                                        }
                                    </th>
                                    <th />
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSites.map(s =>
                                    <TableRowWithOnClick key={s.id} onClick={() => navigate(`/amorgy/sites/${s.id}`)}>
                                        <td>
                                            {s.settings.enabled
                                                ? <Icon name="status_ok" color={colors.icon.success} />
                                                : null
                                            }
                                        </td>
                                        <td style={{ textDecoration: s.settings.enabled ? 'none' : 'line-through' }}>
                                            {s.loginName}
                                        </td>
                                        <td>
                                            {s.organization
                                                ?   s.organization.companyInfo.name
                                                    ?   s.organization?.companyInfo.name
                                                    :  <i>intet navn angivet</i>
                                                : <i>-</i>
                                            }
                                        </td>
                                        {activeUser && activeUser.partner===null &&
                                            <td>
                                                {s.organization?.partner
                                                    ?   s.organization?.partner
                                                    :   <></>
                                                }
                                            </td>
                                        }
                                        <td>
                                            <Flex gap={4} flexWrap='wrap'>
                                                {s.settings.flags.filter(flag => flag !== 'skal slettes').map(flag => (
                                                    <InfoChip key={flag} colorIndex={s.settings.flags.indexOf(flag)}>
                                                        {flag}
                                                    </InfoChip>
                                                ))}
                                                {s.settings.flags.includes('skal slettes') && (
                                                    <InfoChip key="skal slettes" variant="error">skal slettes</InfoChip>
                                                )}
                                            </Flex>
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()} style={{ width: '0.1%', whiteSpace: 'nowrap' }}>
                                            <ButtonRow variant="right-aligned" style={{ alignItems: 'center' }}>
                                                <ImpersonateModalButton
                                                    siteId={s.id}
                                                    siteEnabled={s.settings.enabled} />
                                            </ButtonRow>

                                        </td>
                                    </TableRowWithOnClick>
                                )}
                            </tbody>
                        </Table>

                    </Flex>
                } />

        </AmorgAdminPageProvider>
    );
}

function CreateSiteModalButton (
    props: {
        activeUser: AmorgAdminToken;
        onCreated: () => void;
    }): JSX.Element
{
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(false);
    const [orgs, setOrgs] = React.useState<OrganizationListView[]>([]);
    const [siteId, setSiteId] = React.useState('');
    const [selectedOrgId, setSelectedOrgId] = React.useState('');
    const [error, setError] = React.useState<'none' | 'createOrg' | 'updateOrg' | 'createSite-invalidId' | 'createSite-inUse' | 'createSite-other'>('none');
    const [showModal, setShowModal] = React.useState(false);

    async function handleClickShow()
    {
        await setOrgs(await getOrganizations(() => navigate('/amorgy/login')));
        setShowModal(true);
    }

    async function handleClickCreateSite ()
    {
        setLoading(true);

        if(!fieldValidation.site.id(siteId))
        {
            setError('createSite-invalidId');
            setLoading(false);
            return;
        }

        // Note - may still fail later if site is in use by another partner
        const sites = await getAllSites(() => navigate('/amorgy/login'));
        if(sites.some(site => site.id === siteId))
        {
            setError('createSite-inUse');
            setLoading(false);
            return;
        }

        let organizationId = selectedOrgId;
        if(!organizationId)
        {

            organizationId = siteId + '-' + rng.getRandomId();
            const orgCreateResult = await createOrganization(organizationId, () => navigate('/amorgy/login'));
            if(orgCreateResult.status !== 'success')
            {
                setError('createOrg');
                setLoading(false);
                return;
            }

            const orgUpdateResult = await updateOrganization(
                organizationId,
                {
                    ...getDefaultCompanyInfo(),
                    name: 'Organisation for ' + siteId
                },
                props.activeUser.partner,
                 () => navigate('/amorgy/login')
            );
            if(!orgUpdateResult)
            {
                setError('updateOrg');
                setLoading(false);
                return;
            }
        }

        const createSiteResult = await createSite(siteId, organizationId, () => navigate('/amorgy/login'));
        if(!createSiteResult)
        {
            setError('createSite-other');
            setLoading(false);
            return;
        }
        else
        {
            setError('none');
            setSelectedOrgId('');
            setSiteId('');
            setLoading(false);
            setShowModal(false);
            props.onCreated();
        }
    }

    async function handleClickClose ()
    {
        setShowModal(false);
        setError('none');
        setSiteId('');
        setSelectedOrgId('');
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            <Button icon="actions_plus" variant="filled" text="Opret nyt site" onClick={handleClickShow} emphasize />

            <Modal
                title={"Opret nyt site"}
                content={
                    <Flex flexDirection="column" gap={16}>

                        {(error === 'createOrg' || error === 'updateOrg') &&
                            <ErrorLabel>
                                Fejl ved oprettelse af organisation
                            </ErrorLabel>
                        }

                        {error === 'createSite-invalidId' &&
                            <ErrorLabel>
                                Ugyldigt site ID
                            </ErrorLabel>
                        }

                        {error === 'createSite-other' &&
                            <ErrorLabel>
                                Fejl ved oprettelse af site
                            </ErrorLabel>
                        }

                        {error === 'createSite-inUse' &&
                            <ErrorLabel>
                                Site ID er allerede i brug
                            </ErrorLabel>
                        }

                        <TextField
                            value={siteId}
                            onChange={setSiteId}
                            label="Site ID/ Navn"
                        />


                        <SelectField
                            label="Organisation"
                            selectedValue={selectedOrgId}
                            emptyText='Opret ny organisation...'
                            options={orgs.map(org => ({ value: org.id, text: org.companyInfo.name || org.id }))} 
                            onChange={setSelectedOrgId}
                        />

                        <Flex flexDirection="row" justifyContent="space-between">
                            <Button variant="text" text='Annullér'
                                onClick={handleClickClose} />
                            <Button variant="filled" text='Opret'
                                onClick={handleClickCreateSite} />
                        </Flex>
                    </Flex>
                }
                showModal={showModal}
                onCancel={handleClickClose} />


        </>
    );
}